import React from 'react';
import { LayoutDashboard, TrendingUp, MapPin } from 'lucide-react';

export default function DashboardMockup({ size = 'sm' }) {
  const isLg = size === 'lg';

  const stats = [
    {
      label: 'Active Projects',
      value: '8',
      sub: '+2 this month',
      color: 'text-blue-400',
      bg: 'bg-blue-500/15',
    },
    {
      label: 'Total Invested',
      value: '$2.4M',
      sub: 'across 12 flips',
      color: 'text-violet-400',
      bg: 'bg-violet-500/15',
    },
    {
      label: 'Projected Profit',
      value: '$485K',
      sub: '+18% vs last year',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/15',
    },
    {
      label: 'Avg. ROI',
      value: '24.5%',
      sub: 'above target',
      color: 'text-amber-400',
      bg: 'bg-amber-500/15',
    },
  ];

  const projects = [
    {
      address: '1428 Elm Street',
      city: 'Dallas, TX',
      progress: 72,
      profit: '$38K',
      status: 'Rehab',
    },
    {
      address: '742 Evergreen Terrace',
      city: 'Phoenix, AZ',
      progress: 45,
      profit: '$52K',
      status: 'Listed',
    },
  ];

  return (
    <div className={`w-full ${isLg ? 'p-4' : 'p-3'} bg-slate-800/80 rounded-xl`}>
      {/* Mini header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-blue-500/20 flex items-center justify-center">
          <LayoutDashboard size={11} className="text-blue-400" />
        </div>
        <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white`}>
          The Hub
        </span>
      </div>

      {/* Stat cards */}
      <div className={`grid grid-cols-2 ${isLg ? 'md:grid-cols-4 gap-3 mb-4' : 'gap-1.5 mb-2.5'}`}>
        {stats.map((stat, i) => (
          <div key={i} className={`${stat.bg} rounded-lg ${isLg ? 'px-3 py-2.5' : 'px-2 py-1.5'}`}>
            <p className="text-[7px] text-slate-500 uppercase tracking-wider">{stat.label}</p>
            <p className={`${isLg ? 'text-base' : 'text-[11px]'} font-extrabold ${stat.color}`}>
              {stat.value}
            </p>
            {isLg && <p className="text-[8px] text-slate-500 mt-0.5">{stat.sub}</p>}
          </div>
        ))}
      </div>

      {/* Project cards */}
      <div className={`grid ${isLg ? 'grid-cols-2 gap-3' : 'grid-cols-1 gap-1.5'}`}>
        {projects.slice(0, isLg ? 2 : 1).map((proj, i) => (
          <div key={i} className={`bg-slate-700/40 rounded-lg ${isLg ? 'p-3' : 'p-2'}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1">
                <MapPin size={9} className="text-slate-500" />
                <span className={`${isLg ? 'text-[11px]' : 'text-[9px]'} font-semibold text-white`}>
                  {proj.address}
                </span>
              </div>
              <span
                className={`text-[7px] font-semibold px-1.5 py-0.5 rounded-full ${
                  proj.status === 'Rehab'
                    ? 'bg-amber-500/15 text-amber-400'
                    : 'bg-blue-500/15 text-blue-400'
                }`}
              >
                {proj.status}
              </span>
            </div>
            <p className="text-[8px] text-slate-500 mb-1.5">{proj.city}</p>
            <div
              className={`w-full ${isLg ? 'h-1.5' : 'h-1'} bg-slate-600/50 rounded-full overflow-hidden mb-1`}
            >
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                style={{ width: `${proj.progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[7px] text-slate-500">{proj.progress}% complete</span>
              <div className="flex items-center gap-0.5">
                <TrendingUp size={8} className="text-emerald-400" />
                <span className="text-[8px] font-bold text-emerald-400">{proj.profit}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart (lg only) */}
      {isLg && (
        <div className="mt-3 bg-slate-700/30 rounded-lg p-3">
          <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
            Monthly Profit Trend
          </p>
          <div className="h-16 flex items-end gap-1.5">
            {[30, 45, 35, 55, 42, 65, 58, 72, 60, 78, 70, 85].map((h, i) => (
              <div
                key={i}
                className="flex-1 bg-gradient-to-t from-violet-500/60 to-blue-500/40 rounded-t-sm"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
